import { parseCoordinateLine } from "./coordinates"

const COORDINATE_START = /^(?:[+-]?\d|[NSEW](?=\s*[+-]?\d)|ZONE\b|MGRS\b|LAT\.?\b|BEGINNING\s+AT\s+LAT\b)/i

function unquoteName(value) {
  const name = value.trim()
  if ((name.startsWith('"') && name.endsWith('"')) || (name.startsWith("'") && name.endsWith("'"))) {
    return name.slice(1, -1).trim()
  }
  return name
}

function errorRow(lineNo, source, message) {
  return { lineNo, source, ok: false, error: `Line ${lineNo}: ${message}` }
}

function validRow(lineNo, source, name, parsed) {
  return {
    lineNo,
    source,
    ok: true,
    name,
    lat: parsed.lat,
    lng: parsed.lng,
    ddm: parsed.ddm,
  }
}

export function parseBatchPointLine(line, lineNo) {
  const source = String(line ?? "").trim()
  if (!source) return errorRow(lineNo, source, "Point name and coordinate are required.")

  const wholeLine = parseCoordinateLine(source, { strict: true })
  if (!wholeLine.error) {
    return errorRow(lineNo, source, "Point name is required.")
  }

  if (source.startsWith('"') || source.startsWith("'")) {
    const quote = source[0]
    const closingQuote = source.indexOf(quote, 1)
    if (closingQuote < 0) return errorRow(lineNo, source, "Point name has no closing quote.")

    const name = unquoteName(source.slice(0, closingQuote + 1))
    if (!name) return errorRow(lineNo, source, "Point name is required.")

    const coordinate = source.slice(closingQuote + 1).trim()
    const parsed = parseCoordinateLine(coordinate, { strict: true })
    if (parsed.error) return errorRow(lineNo, source, parsed.error)
    return validRow(lineNo, source, name, parsed)
  }

  const boundaries = []
  for (const match of source.matchAll(/\s+/g)) {
    const index = match.index + match[0].length
    if (COORDINATE_START.test(source.slice(index))) boundaries.push(index)
  }

  for (const index of boundaries) {
    const name = unquoteName(source.slice(0, index))
    if (!name) continue

    const coordinate = source.slice(index)
    const parsed = parseCoordinateLine(coordinate, { strict: true })
    if (!parsed.error) return validRow(lineNo, source, name, parsed)
  }

  return errorRow(lineNo, source, "Could not parse point coordinate.")
}

export function parseBatchPoints(text) {
  const rows = String(text ?? "")
    .split(/\r?\n/)
    .map((line, index) => ({ line, lineNo: index + 1 }))
    .filter(({ line }) => line.trim())
    .map(({ line, lineNo }) => parseBatchPointLine(line, lineNo))

  if (!rows.length) {
    return { rows, validRows: [], error: "Enter at least one batch point." }
  }

  const validRows = rows.filter(({ ok }) => ok)
  const error = rows.filter(({ ok }) => !ok).map((row) => row.error).join("\n")
  return { rows, validRows, error }
}

export function buildBatchPointFeatures(validRows, style, idFactory) {
  return validRows.filter(({ ok }) => ok).map((row) => ({
    type: "Feature",
    id: idFactory(),
    properties: {
      featureType: "marker",
      name: row.name,
      description: style.description,
      color: style.color,
      iconUrl: style.iconUrl,
      iconSymbol: style.iconSymbol,
      iconLabel: style.iconLabel,
    },
    geometry: {
      type: "Point",
      coordinates: [row.lng, row.lat],
    },
  }))
}
